import styled from "@emotion/styled"
import { css } from '@emotion/react'
import facepaint from "facepaint"

import Screen from './c/Screen/Screen'
import ItemInfo from './c/ItemInfo/ItemInfo'

const breakpoints = [440, 768, 1024, 1200, 1400];
const mq = facepaint(breakpoints.map(bp => `@media (min-width: ${bp}px)`));

export const Wrap = styled.div`
  position: relative;
  z-index: 1;
  background: var(--color-dark-blue-500);
`

const contentMQ = css(mq({
  flexDirection: [
    "column", // 0+
    "column", // 440+
    "column", // 768+
    "column", // 1024+
    "row", // 1200+
    "row", // 1400+
  ],
  padding: [
    "40px 0 32px", // 0+
    "40px 0 32px", // 440+
    "44px 0", // 768+
    "64px 0", // 1024+
    "64px 0 96px", // 1200+
    "168px 0 146px", // 1400+
  ],
  gap: [
    16, // 0+
    16, // 440+
    24, // 768+
    24, // 1024+
    24, // 1200+
    64, // 1400+
  ],
  width: [
    "auto", // 0+
    "auto", // 440+
    "auto", // 768+
    "auto", // 1024+
    "auto", // 1200+
    1296, // 1400+
  ],
  maxWidth: [
    "calc(100vw - 32px)", // 0+
    "calc(100vw - 32px)", // 440+
    "calc(100vw - 64px)", // 768+
    "calc(100vw - 80px)", // 1024+
  ],
}))

export const Content = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  position: relative;
  ${contentMQ}
`

const arrowBlockMQ = css(mq({
  position: [
    "static", // 0+
    "static", // 440+
    "static", // 768+
    "static", // 1024+
    "static", // 1200+
    "absolute", // 1400+
  ],
  padding: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    0, // 1200+
    "118px 0", // 1400+
  ],
  "&:before, &:after": {
    display: [
      "none", // 0+
      "none", // 440+
      "none", // 768+
      "none", // 1024+
      "none", // 1200+
      "block", // 1400+
    ],
  },
}))

export const ArrowBlock = styled.div`
  position: absolute;
  top: 42px;
  left: 0;

  &:before {
    content: '';
    position: absolute;
    width: 1px;
    height: 88px;
    top: 0;
    left: calc(50% - 0.5px);
    background: linear-gradient(0deg, rgba(255, 255, 255, 0.50) 34.1%, rgba(255, 255, 255, 0.00) 100%);
  }

  &:after {
    content: '';
    position: absolute;
    width: 1px;
    height: 88px;
    bottom: 0;
    left: calc(50% - 0.5px);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.50) 34.1%, rgba(255, 255, 255, 0.00) 100%);
  }
  ${arrowBlockMQ}
`

const leftSectionMQ = css(mq({
  gap: [
    32, // 0+
    32, // 440+
    32, // 768+
    32, // 1024+
    58, // 1200+
    180, // 1400+
  ],
}))

export const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  ${leftSectionMQ}
`

const messageWrapMQ = css(mq({
  marginLeft: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    0, // 1200+
    125, // 1400+
  ],
  gap: [
    16, // 0+
    16, // 440+
    24, // 768+
  ],
  width: [
    294, // 0+
    294, // 440+
    320, // 768+
    360, // 1024+
  ],

  "& > h2": {
    font: [
      "avar(--accent-770)", // 0+
      "var(--accent-770)", // 440+
      "var(--accent-970)", // 768+
    ],
  },
  "& > p": {
    font: [
      "avar(--accent-550)", // 0+
      "var(--accent-550)", // 440+
      "var(--accent-650)", // 768+
    ],
  }
}))

export const MessageWrap = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--color-white);
  max-width: 100%;
  ${messageWrapMQ}
`

const listWrapMQ = css(mq({
  marginLeft: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    0, // 1200+
    36, // 1400+
  ],
  gap: [
    0, // 0+
    0, // 440+
    0, // 768+
    0, // 1024+
    48, // 1200+
    64, // 1400+
  ],
}))

export const ListWrap = styled.div`
  display: flex;
  align-items: end;
  ${listWrapMQ}
`

const listMQ = css(mq({
  width: [
    "100%", // 0+
    "100%", // 440+
    "100%", // 768+
    "100%", // 1024+
    220, // 1200+
    220, // 1400+
  ],
}))

export const List = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  box-shadow: 0px -1px 0px 0px #538CFF inset;
  ${listMQ}
`

const itemInfoMQ = css(mq({
  display: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "flex", // 1200+
    "flex", // 1400+
  ],
}))

export const MainItemInfo = styled(ItemInfo)`
  gap: 44px;
  ${itemInfoMQ}
`

const innerItemInfoMQ = css(mq({
  display: [
    "flex", // 0+
    "flex", // 440+
    "flex", // 768+
    "flex", // 1024+
    "none", // 1200+
    "none", // 1400+
  ],
}))

export const InnerItemInfo = styled(ItemInfo)`
  gap: 24px;

  & > p {
    max-width: 460px!important;
  }
  ${innerItemInfoMQ}
`

const mainScreenMQ = css(mq({
  display: [
    "none", // 0+
    "none", // 440+
    "none", // 768+
    "none", // 1024+
    "flex", // 1200+
    "flex", // 1400+
  ],
  width: [
    "100%", // 0+
    "100%", // 440+
    "100%", // 768+
    "100%", // 1024+
    "40.2%", // 1200+
    "40.2%", // 1400+
  ],
  height: [
    406, // 0+
    406, // 440+
    406, // 768+
    406, // 1024+
    606, // 1200+
    606, // 1400+
  ],
}))

export const MainScreen = styled(Screen)`
  margin-top: 16px;
  ${mainScreenMQ}
`
